/**
 * From: https://github.com/shikijs/textmate-grammars-themes/blob/main/packages/tm-themes/NOTICE
 * =========================================================================================================
 * Files:   github-dark.json, github-dark-default.json, github-dark-dimmed.json, github-light.json, github-light-default.json
 * License: https://raw.githubusercontent.com/primer/github-vscode-theme/main/LICENSE
 * SPDX:    MIT
 * ---------------------------------------------------------------------------------------------------------
 * MIT License
 */

import { ThemeRegistrationResolved } from 'shiki';

/**
 * This themes is based on Github Dark but the colors themselves have been replaced with Vivid colors using the
 * closest matches.
 */

export default {
  displayName: 'Vonage Dark',
  name: 'vonage-dark',
  semanticHighlighting: true,
  type: 'dark',
  colors: {
    'editor.background': 'var(--code-snippet-bg-color, var(--vvd-color-neutral-900))',
    'editor.foreground': '#999',
  },
  tokenColors: [
    {
      scope: ['comment', 'punctuation.definition.comment', 'string.comment'],
      settings: {
        foreground: '#b3b3b3',
      },
    },
    {
      scope: ['constant.other.placeholder', 'constant.character'],
      settings: {
        foreground: '#f75959',
      },
    },
    {
      scope: [
        'constant',
        'entity.name.constant',
        'variable.other.constant',
        'variable.other.enummember',
        'variable.language',
        'entity',
      ],
      settings: {
        foreground: '#65baff',
      },
    },
    {
      scope: ['entity.name', 'meta.export.default', 'meta.definition.variable'],
      settings: {
        foreground: '#fa9f00',
      },
    },
    {
      scope: [
        'variable.parameter.function',
        'meta.jsx.children',
        'meta.block',
        'meta.tag.attributes',
        'entity.name.constant',
        'meta.object.member',
        'meta.embedded.expression',
      ],
      settings: {
        foreground: '#E6E6E6',
      },
    },
    {
      scope: 'entity.name.function',
      settings: {
        foreground: '#cba1fa',
      },
    },
    {
      scope: ['entity.name.tag', 'support.class.component'],
      settings: {
        foreground: '#86e090',
      },
    },
    {
      scope: 'keyword',
      settings: {
        foreground: '#f75959',
      },
    },
    {
      scope: ['storage', 'storage.type'],
      settings: {
        foreground: '#f75959',
      },
    },
    {
      scope: ['storage.modifier.package', 'storage.modifier.import', 'storage.type.java'],
      settings: {
        foreground: '#E6E6E6',
      },
    },
    {
      scope: ['string', 'string punctuation.section.embedded source'],
      settings: {
        foreground: '#9dd2fe',
      },
    },
    {
      scope: 'support',
      settings: {
        foreground: '#65baff',
      },
    },
    {
      scope: 'meta.property-name',
      settings: {
        foreground: '#65baff',
      },
    },
    {
      scope: 'variable',
      settings: {
        foreground: '#fa9f00',
      },
    },
    {
      scope: 'variable.other',
      settings: {
        foreground: '#E6E6E6',
      },
    },
    {
      scope: 'invalid.broken',
      settings: {
        fontStyle: 'italic',
        foreground: '#fe9696',
      },
    },
    {
      scope: 'invalid.deprecated',
      settings: {
        fontStyle: 'italic',
        foreground: '#fe9696',
      },
    },
    {
      scope: 'invalid.illegal',
      settings: {
        fontStyle: 'italic',
        foreground: '#fe9696',
      },
    },
    {
      scope: 'invalid.unimplemented',
      settings: {
        fontStyle: 'italic',
        foreground: '#fe9696',
      },
    },
    {
      scope: 'carriage-return',
      settings: {
        background: '#f75959',
        content: '^M',
        fontStyle: 'italic underline',
        foreground: '#cdd9e5',
      },
    },
    {
      scope: 'message.error',
      settings: {
        foreground: '#fe9696',
      },
    },
    {
      scope: 'string variable',
      settings: {
        foreground: '#65baff',
      },
    },
    {
      scope: ['source.regexp', 'string.regexp'],
      settings: {
        foreground: '#9dd2fe',
      },
    },
    {
      scope: [
        'string.regexp.character-class',
        'string.regexp constant.character.escape',
        'string.regexp source.ruby.embedded',
        'string.regexp string.regexp.arbitrary-repitition',
      ],
      settings: {
        foreground: '#9dd2fe',
      },
    },
    {
      scope: 'string.regexp constant.character.escape',
      settings: {
        fontStyle: 'bold',
        foreground: '#86e090',
      },
    },
    {
      scope: 'support.constant',
      settings: {
        foreground: '#65baff',
      },
    },
    {
      scope: 'support.variable',
      settings: {
        foreground: '#65baff',
      },
    },
    {
      scope: 'support.type.property-name.json',
      settings: {
        foreground: '#86e090',
      },
    },
    {
      scope: 'meta.module-reference',
      settings: {
        foreground: '#65baff',
      },
    },
    {
      scope: 'punctuation.definition.list.begin.markdown',
      settings: {
        foreground: '#fa9f00',
      },
    },
    {
      scope: ['markup.heading', 'markup.heading entity.name'],
      settings: {
        fontStyle: 'bold',
        foreground: '#65baff',
      },
    },
    {
      scope: 'markup.quote',
      settings: {
        foreground: '#86e090',
      },
    },
    {
      scope: 'markup.italic',
      settings: {
        fontStyle: 'italic',
        foreground: '#E6E6E6',
      },
    },
    {
      scope: 'markup.bold',
      settings: {
        fontStyle: 'bold',
        foreground: '#E6E6E6',
      },
    },
    {
      scope: ['markup.underline'],
      settings: {
        fontStyle: 'underline',
      },
    },
    {
      scope: ['markup.strikethrough'],
      settings: {
        fontStyle: 'strikethrough',
      },
    },
    {
      scope: 'markup.inline.raw',
      settings: {
        foreground: '#65baff',
      },
    },
    {
      scope: ['markup.deleted', 'meta.diff.header.from-file', 'punctuation.definition.deleted'],
      settings: {
        background: '#5d0f12',
        foreground: '#fe9696',
      },
    },
    {
      scope: ['punctuation.section.embedded'],
      settings: {
        foreground: '#f75959',
      },
    },
    {
      scope: ['markup.inserted', 'meta.diff.header.to-file', 'punctuation.definition.inserted'],
      settings: {
        background: '#113417',
        foreground: '#86e090',
      },
    },
    {
      scope: ['markup.changed', 'punctuation.definition.changed'],
      settings: {
        background: '#682d0f',
        foreground: '#fa9f00',
      },
    },
    {
      scope: ['markup.ignored', 'markup.untracked'],
      settings: {
        background: '#65baff',
        foreground: '#2d333b',
      },
    },
    {
      scope: 'meta.diff.range',
      settings: {
        fontStyle: 'bold',
        foreground: '#cba1fa',
      },
    },
    {
      scope: 'meta.diff.header',
      settings: {
        foreground: '#65baff',
      },
    },
    {
      scope: 'meta.separator',
      settings: {
        fontStyle: 'bold',
        foreground: '#65baff',
      },
    },
    {
      scope: 'meta.output',
      settings: {
        foreground: '#65baff',
      },
    },
    {
      scope: [
        'brackethighlighter.tag',
        'brackethighlighter.curly',
        'brackethighlighter.round',
        'brackethighlighter.square',
        'brackethighlighter.angle',
        'brackethighlighter.quote',
      ],
      settings: {
        foreground: '#b3b3b3',
      },
    },
    {
      scope: 'brackethighlighter.unmatched',
      settings: {
        foreground: '#fe9696',
      },
    },
    {
      scope: ['constant.other.reference.link', 'string.other.link'],
      settings: {
        foreground: '#9dd2fe',
      },
    },
  ],
} as unknown as ThemeRegistrationResolved;
